import { useEffect, useMemo, useRef, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ChevronLeft, X } from 'lucide-react';
import { Dialog, DialogContent, DialogTitle } from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { cn } from '@/lib/utils';

export type AgentAccentColor = 'sky' | 'emerald' | 'blue' | 'pink' | 'purple' | 'amber';

export interface AgentSectionDef {
  id: string;
  label: string;
  description?: string;
  icon: ReactNode;
  isWarning?: boolean;
  isInvalid?: boolean;
  isComplete?: boolean;
  badge?: ReactNode;
  hidden?: boolean;
}

export interface AgentConfigShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  headerBadge?: ReactNode;
  accentColor?: AgentAccentColor;
  sections: AgentSectionDef[];
  activeSectionId: string;
  onSectionChange: (id: string) => void;
  footerLeft?: ReactNode;
  footerRight: ReactNode;
  onSave?: () => void;
  preventClose?: boolean;
  children: ReactNode;
  contentClassName?: string;
}

const accentMap: Record<AgentAccentColor, { active: string; iconBg: string; ring: string; track: string }> = {
  sky:     { active: 'bg-sky-50 dark:bg-sky-900/20 text-sky-700 dark:text-sky-300',         iconBg: 'bg-sky-100 dark:bg-sky-900/30 text-sky-600',         ring: 'ring-sky-400',     track: 'bg-sky-500' },
  emerald: { active: 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300', iconBg: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600', ring: 'ring-emerald-400', track: 'bg-emerald-500' },
  blue:    { active: 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300',     iconBg: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600',     ring: 'ring-blue-400',    track: 'bg-blue-500' },
  pink:    { active: 'bg-pink-50 dark:bg-pink-900/20 text-pink-700 dark:text-pink-300',     iconBg: 'bg-pink-100 dark:bg-pink-900/30 text-pink-600',     ring: 'ring-pink-400',    track: 'bg-pink-500' },
  purple:  { active: 'bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300', iconBg: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600', ring: 'ring-purple-400',  track: 'bg-purple-500' },
  amber:   { active: 'bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300', iconBg: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600', ring: 'ring-amber-400',   track: 'bg-amber-500' },
};

function SectionStatusDot({ section }: { section: AgentSectionDef }) {
  if (section.isInvalid) {
    return <span className="w-1.5 h-1.5 rounded-full bg-red-500 flex-shrink-0" title="Dati mancanti" />;
  }
  if (section.isWarning) {
    return <span className="w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" title="Attenzione" />;
  }
  if (section.isComplete) {
    return (
      <span className="w-3.5 h-3.5 rounded-full bg-emerald-500 text-white inline-flex items-center justify-center flex-shrink-0" title="Completato">
        <Check className="w-2.5 h-2.5" />
      </span>
    );
  }
  return null;
}

export function AgentConfigShell({
  open,
  onOpenChange,
  title,
  subtitle,
  icon,
  headerBadge,
  accentColor = 'sky',
  sections,
  activeSectionId,
  onSectionChange,
  footerLeft,
  footerRight,
  onSave,
  preventClose = true,
  children,
  contentClassName,
}: AgentConfigShellProps) {
  const accent = accentMap[accentColor];
  const visibleSections = useMemo(() => sections.filter(s => !s.hidden), [sections]);
  const activeIndex = visibleSections.findIndex(s => s.id === activeSectionId);
  const activeSection = visibleSections[activeIndex] ?? visibleSections[0];
  const contentRef = useRef<HTMLDivElement | null>(null);
  const mobileNavRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    contentRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    const el = mobileNavRef.current?.querySelector<HTMLElement>(`[data-section-id="${activeSectionId}"]`);
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeSectionId]);

  useEffect(() => {
    if (!open || !onSave) return;
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        onSave();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [open, onSave]);

  const totalSteps = visibleSections.length;
  const progressPct = totalSteps > 1 ? ((activeIndex + 1) / totalSteps) * 100 : 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={cn(
          'w-[99vw] sm:!w-[88vw] !max-w-[1280px] sm:!max-w-[1280px] !h-[88vh] sm:!h-[88vh] !max-h-[860px] sm:!max-h-[860px] !p-0 sm:!p-0 !gap-0 sm:!gap-0 flex flex-col overflow-hidden [&>button.absolute]:hidden',
          contentClassName,
        )}
        onInteractOutside={preventClose ? (e) => e.preventDefault() : undefined}
        onPointerDownOutside={preventClose ? (e) => e.preventDefault() : undefined}
        onEscapeKeyDown={preventClose ? (e) => e.preventDefault() : undefined}
      >
        <DialogTitle className="sr-only">Configurazione agente AI</DialogTitle>
        {/* Header */}
        <header className="flex items-center gap-3 px-4 sm:px-6 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          {icon && (
            <div className={cn('flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0', accent.iconBg)}>
              {icon}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2 min-w-0">
              <h2 className="text-sm sm:text-base font-semibold text-gray-900 dark:text-gray-100 truncate">{title}</h2>
              {headerBadge}
            </div>
            {subtitle && (
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{subtitle}</p>
            )}
          </div>
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800"
            onClick={() => onOpenChange(false)}
            aria-label="Chiudi"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {/* Mobile/tablet horizontal section nav */}
        <div className="lg:hidden border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div ref={mobileNavRef} className="flex items-center gap-1 overflow-x-auto px-3 py-2 scrollbar-thin">
            {visibleSections.map((s, idx) => {
              const isActive = s.id === activeSection?.id;
              return (
                <button
                  key={s.id}
                  type="button"
                  data-section-id={s.id}
                  onClick={() => onSectionChange(s.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap flex-shrink-0 transition-colors border',
                    isActive
                      ? cn(accent.active, 'border-transparent shadow-sm')
                      : 'text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
                  )}
                >
                  <span className="inline-flex w-4 h-4 items-center justify-center">{s.icon}</span>
                  <span>{s.label}</span>
                  <SectionStatusDot section={s} />
                  <span className="text-[10px] text-gray-400">{idx + 1}</span>
                </button>
              );
            })}
          </div>
          <div className="h-0.5 bg-gray-100 dark:bg-gray-800">
            <motion.div
              className={cn('h-full', accent.track)}
              initial={false}
              animate={{ width: `${progressPct}%` }}
              transition={{ duration: 0.25 }}
            />
          </div>
        </div>

        {/* Body: sidebar + content */}
        <div className="flex-1 min-h-0 flex w-full min-w-0">
          {/* Desktop sidebar */}
          <aside className="hidden lg:flex flex-col w-52 xl:w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-900/50 overflow-y-auto">
            <nav className="p-3 space-y-1">
              {visibleSections.map((s, idx) => {
                const isActive = s.id === activeSection?.id;
                return (
                  <button
                    key={s.id}
                    type="button"
                    onClick={() => onSectionChange(s.id)}
                    className={cn(
                      'group w-full text-left flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors min-w-0 relative',
                      isActive
                        ? cn(accent.active, 'font-medium')
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                    )}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="agent-shell-active"
                        className={cn('absolute left-0 top-1 bottom-1 w-0.5 rounded-r', accent.track)}
                      />
                    )}
                    <span className="flex-shrink-0 mt-0.5 inline-flex w-5 h-5 items-center justify-center">
                      {s.icon}
                    </span>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-center gap-1.5 min-w-0">
                        <span className="block truncate">{s.label}</span>
                        <SectionStatusDot section={s} />
                        {s.badge}
                      </span>
                      {s.description && (
                        <span className="block text-xs text-gray-500 dark:text-gray-500 truncate font-normal">
                          {s.description}
                        </span>
                      )}
                    </span>
                    <span className="text-[10px] text-gray-400 group-hover:text-gray-500 flex-shrink-0 mt-0.5">{idx + 1}</span>
                  </button>
                );
              })}
            </nav>
          </aside>

          {/* Content area */}
          <main
            ref={contentRef}
            className="flex-1 min-w-0 min-h-0 overflow-y-auto overflow-x-hidden bg-white dark:bg-gray-950"
          >
            <div className="w-full min-w-0 px-4 sm:px-5 py-4 sm:py-5">
              {/* Section header */}
              {activeSection && (
                <div className="mb-4 sm:mb-5 pb-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>Sezione {activeIndex + 1} di {totalSteps}</span>
                  </div>
                  <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <span className={cn('inline-flex items-center justify-center w-7 h-7 rounded-md', accent.iconBg)}>
                      {activeSection.icon}
                    </span>
                    {activeSection.label}
                  </h3>
                  {activeSection.description && (
                    <p className="mt-1 text-xs sm:text-sm text-gray-500 dark:text-gray-400">{activeSection.description}</p>
                  )}
                </div>
              )}

              <AnimatePresence mode="wait">
                <motion.div
                  key={activeSectionId}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -4 }}
                  transition={{ duration: 0.18 }}
                >
                  {children}
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>

        {/* Footer */}
        <footer className="flex flex-wrap items-center justify-between gap-2 px-3 sm:px-6 py-2 sm:py-3 border-t border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2 min-w-0">
            {footerLeft}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0 ml-auto [&>button]:flex-1 sm:[&>button]:flex-initial">
            {footerRight}
          </div>
        </footer>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Helper navigation buttons for prev/next pattern within a section list.
 */
export interface AgentShellNavProps {
  sections: AgentSectionDef[];
  activeSectionId: string;
  onSectionChange: (id: string) => void;
  prevLabel?: string;
  nextLabel?: string;
}

export function AgentShellPrevNext({ sections, activeSectionId, onSectionChange, prevLabel = 'Indietro', nextLabel = 'Avanti' }: AgentShellNavProps) {
  const visible = sections.filter(s => !s.hidden);
  const idx = visible.findIndex(s => s.id === activeSectionId);
  const prev = visible[idx - 1];
  const next = visible[idx + 1];
  return (
    <>
      <Button variant="outline" size="sm" disabled={!prev} onClick={() => prev && onSectionChange(prev.id)}>
        <ChevronLeft className="h-3.5 w-3.5 mr-1" />
        {prevLabel}
      </Button>
      <Button variant="outline" size="sm" disabled={!next} onClick={() => next && onSectionChange(next.id)}>
        {nextLabel}
      </Button>
    </>
  );
}

export default AgentConfigShell;
